"use client";

import React, { useState } from "react";
import { ArrowLeft, ArrowRight, BookOpen, Calendar, CircleCheck as CheckCircle2, ChevronRight, Cpu, Factory, Layers, Lightbulb, Laptop, Presentation, Quote, Rocket, Settings, Target, Users, Wrench, DollarSign, Compass, GitFork, Activity, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function RoboticsCourseOverview() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const content = {
    vi: {
      breadcrumbs: [{
        label: "Trang chủ",
        to: "/"
      }, {
        label: "Lộ trình Robotics",
        to: "/RoboticsLearningPathwayPage"
      }, {
        label: "Robotics 7+ tuổi",
        to: "#"
      }, {
        label: "HP2 – Robot biết cảm nhận",
        to: null
      }],
      courseCode: "HP2",
      courseName: "HP2 – Robot biết cảm nhận",
      tagline: "Khám phá thế giới cảm biến và logic điều khiển thông minh",
      quote: "Robot bắt đầu cảm nhận và phản hồi môi trường.",
      heroPrimaryCta: "ĐĂNG KÝ TƯ VẤN",
      heroSecondaryCta: "XEM TOÀN BỘ LỘ TRÌNH",
      stats: [{
        label: "Độ tuổi",
        value: "Từ 7 tuổi",
        icon: BookOpen
      }, {
        label: "Bộ kit thực hành",
        value: "WhalesBot AI Module 1S",
        icon: Cpu
      }, {
        label: "Thời lượng",
        value: "12 buổi × 90 phút",
        icon: Calendar
      }, {
        label: "Quy mô lớp",
        value: "2–3 học sinh/bộ kit",
        icon: Users
      }, {
        label: "Điều kiện tiên quyết",
        value: "Hoàn thành HP1",
        icon: CheckCircle2
      }],
      audienceTitle: "Khóa học này dành cho ai?",
      audienceStatement: "Dành cho học viên đã hoàn thành HP1. HP2 mở rộng sang cảm biến hồng ngoại, cảm biến chạm và logic điều kiện rẽ nhánh.",
      audiencePoints: ["Học viên đã nắm chắc nguyên lý lắp ráp cơ bản từ HP1 và sẵn sàng kết nối cảm biến.", "Rèn luyện tư duy lập trình điều khiển tự động với cảm biến hồng ngoại và cảm biến chạm.", "Làm chủ cấu trúc thuật toán rẽ nhánh: if, if-else, wait until, biến số và bộ đếm.", "Chuyển từ cơ cấu chuyển động thuần túy sang robot tương tác có phản xạ thông minh."],
      focusHeaders: {
        sensorsLogic: "Cảm biến & Logic lập trình",
        mechanics: "Cấu trúc cơ khí nâng cao"
      },
      sensorsLogicItems: [{
        name: "Infrared Sensor",
        desc: "Cảm biến hồng ngoại nhận diện vật cản và khoảng cách"
      }, {
        name: "Touch Sensor",
        desc: "Cảm biến chạm kích hoạt và chuyển đổi trạng thái"
      }, {
        name: "Lệnh if & if–else",
        desc: "Cấu trúc điều kiện rẽ nhánh hành vi của robot"
      }, {
        name: "Wait until",
        desc: "Lệnh tạm dừng chờ tín hiệu sự kiện hợp lệ"
      }, {
        name: "Comparison",
        desc: "Các phép so sánh điều kiện: lớn hơn, nhỏ hơn, bằng"
      }, {
        name: "Counters & Variables",
        desc: "Bộ đếm dữ liệu và biến số quản lý trạng thái hệ thống"
      }],
      mechanicsItems: [{
        name: "Bevel gears",
        desc: "Bánh răng nón truyền chuyển động đổi hướng 90 độ"
      }, {
        name: "Chain drive",
        desc: "Bộ truyền động xích truyền lực qua khoảng cách lớn"
      }, {
        name: "Worm gear",
        desc: "Bộ truyền bánh vít – trục vít tự hãm và tăng mô-men"
      }, {
        name: "Four-bar linkage",
        desc: "Cơ cấu 4 khâu bản lề tạo quỹ đạo chuyển động phức hợp"
      }],
      timelineTitle: "Lộ trình 12 buổi học (6 giai đoạn)",
      timelineSubtitle: "Chương trình chuyên sâu tích hợp cơ cấu truyền động và thuật toán cảm biến",
      timelineStages: [{
        stage: "Giai đoạn 1",
        sessions: "Buổi 1 – 2",
        title: "Bevel gears & Chain drive",
        focus: "Khám phá truyền động bánh răng nón đổi hướng góc vuông và cơ cấu xích tải truyền lực."
      }, {
        stage: "Giai đoạn 2",
        sessions: "Buổi 3 – 4",
        title: "Worm gear & Four-bar linkage",
        focus: "Chinh phục bộ truyền trục vít bánh vít tự hãm và cơ cấu 4 khâu bản lề đa chuyển động."
      }, {
        stage: "Giai đoạn 3",
        sessions: "Buổi 5 – 6",
        title: "Touch sensor & Wait until",
        focus: "Ứng dụng cảm biến chạm làm công tắc hành trình và lệnh Wait until chờ sự kiện kích hoạt."
      }, {
        stage: "Giai đoạn 4",
        sessions: "Buổi 7 – 8",
        title: "Infrared sensor & If / If-else",
        focus: "Khám phá cảm biến hồng ngoại phát hiện vật cản kết hợp thuật toán rẽ nhánh if và if-else."
      }, {
        stage: "Giai đoạn 5",
        sessions: "Buổi 9 – 10",
        title: "Comparisons, Counters & Variables",
        focus: "Tích hợp phép so sánh giá trị cảm biến, bộ đếm sự kiện và biến số lưu trữ trạng thái."
      }, {
        stage: "Giai đoạn 6",
        sessions: "Buổi 11 – 12",
        title: "Tích hợp hệ thống & Dự án cuối khóa",
        focus: "Tổng hợp toàn diện cơ cấu cơ khí và logic cảm biến, hoàn thiện mô hình và thuyết trình."
      }],
      methodologyTitle: "Phương pháp học tập 4 bước",
      methodologySubtitle: "Chu trình chuẩn STEAM giúp học sinh tiếp thu và làm chủ kiến thức vững chắc",
      methodologySteps: [{
        step: "Bước 1",
        title: "Khởi động",
        desc: "Tìm hiểu tình huống thực tế, đặt vấn đề kỹ thuật và khơi gợi tư duy giải pháp công nghệ.",
        icon: Lightbulb
      }, {
        step: "Bước 2",
        title: "Lắp ráp",
        desc: "Xây dựng mô hình cơ khí vững chắc, ghép nối chuẩn xác các cơ cấu bánh răng và trục truyền.",
        icon: Wrench
      }, {
        step: "Bước 3",
        title: "Lập trình",
        desc: "Xây dựng thuật toán, kết nối khối lệnh logic điều kiện và gán phản xạ thông minh cho cảm biến.",
        icon: Cpu
      }, {
        step: "Bước 4",
        title: "Trình bày",
        desc: "Thực nghiệm kiểm thử, tinh chỉnh thông số vận hành và thuyết trình giới thiệu sản phẩm.",
        icon: Presentation
      }],
      equipmentTitle: "Trang thiết bị học tập",
      equipmentSubtitle: "Bộ công cụ tiêu chuẩn quốc tế trang bị tại phòng lab ROBOTO",
      equipmentName: "WhalesBot AI Module 1S",
      equipmentCostLabel: "Chi phí thiết bị tham khảo",
      equipmentCostValue: "~6.500.000 VNĐ / bộ kit",
      equipmentCostNote: "Lưu ý: Đây là chi phí thiết bị tham khảo cho phụ huynh có nhu cầu sở hữu, không phải học phí khóa học.",
      equipmentPcRequired: "Yêu cầu máy tính",
      equipmentPcNote: "Cần máy tính (Laptop/PC) để thao tác trên phần mềm lập trình kéo thả WhalesBot.",
      groupModelTitle: "Mô hình học nhóm nhỏ",
      groupModelSubtitle: "Thiết kế tối ưu hiệu quả tương tác và thực hành thực tế",
      groupModelIdealTitle: "Lý tưởng: 2 học sinh / bộ kit",
      groupModelIdealDesc: "Tối ưu hóa thời gian chạm tay vào thiết bị, rèn luyện kỹ năng phân vai (1 kỹ sư cơ khí, 1 kỹ sư lập trình) và phối hợp nhóm hiệu quả.",
      groupModelOptimizedTitle: "Tối ưu chi phí: 3 học sinh / bộ kit",
      groupModelOptimizedDesc: "Giải pháp linh hoạt giúp tối ưu chi phí đầu tư thiết bị mà vẫn đảm bảo học sinh được luân phiên thực hành và kiểm thử sản phẩm.",
      finalProjectTitle: "Dự án mẫu cuối khóa",
      finalProjectTag: "DỰ ÁN TÍCH HỢP HP2",
      finalProjectName: "Nhà máy tí hon (Tiny Factory)",
      finalProjectDesc: "Dự án tiêu biểu kết hợp đầy đủ các kiến thức trọng tâm của HP2: hệ truyền động cơ khí (bánh xích, bánh răng nón, trục vít), cơ cấu 4 khâu cùng hệ thống cảm biến hồng ngoại và cảm biến chạm điều khiển bằng logic rẽ nhánh và bộ đếm.",
      finalProjectHighlights: ["Hệ thống băng chuyền cơ khí vận hành với bánh xích và bánh răng nón truyền động vuông góc", "Trục nâng hạ tự hãm sử dụng bộ truyền trục vít – bánh vít", "Cảm biến hồng ngoại tự động phát hiện hàng hóa trên dây chuyền và dừng đúng vị trí", "Cảm biến chạm hoạt động như nút dừng khẩn cấp hoặc công tắc xác nhận chu trình", "Bộ đếm và biến số thống kê số lượng thành phẩm hoàn thành"],
      pathwayTitle: "Vị trí trong lộ trình Robotics",
      pathwaySubtitle: "Hệ thống 10 cấp độ đào tạo chuẩn từ mầm non đến trung học cơ sở",
      currentBadge: "Khóa hiện tại",
      nextCourseTitle: "Khóa học tiếp theo",
      nextCourseBadge: "BƯỚC KẾ TIẾP: HP3",
      nextCourseName: "HP3 – Xe robot đầu tiên",
      nextCourseDesc: "Sau khi thành thạo cơ cấu và phản xạ cảm biến ở HP2, học viên nâng cấp lên việc xây dựng xe robot tự hành có bánh xe di chuyển độc lập, cảm biến bám line và định hướng sa bàn.",
      nextCourseCta: "XEM HP3 →",
      prevBtn: "← HP1 — Kỹ sư cơ khí nhí",
      nextBtn: "HP3 — Xe robot đầu tiên →",
      langToggleVI: "Tiếng Việt",
      langToggleEN: "English",
      logicDiagramTitle: "Sơ đồ Logic Điều khiển Cảm biến (If / Else)",
      logicDiagramSubtitle: "Mô phỏng chu trình xử lý tín hiệu từ Cảm biến → Thuật toán Rẽ nhánh → Hành vi Robot",
      sensorLabelIR: "Cảm biến Hồng ngoại (Infrared)",
      sensorLabelTouch: "Cảm biến Chạm (Touch)",
      conditionNode: "Điều kiện: Vật cản < 15cm?",
      actionTrue: "ĐÚNG (If): Dừng động cơ & Phát âm thanh cảnh báo",
      actionFalse: "SAI (Else): Tiếp tục di chuyển băng chuyền"
    },
    en: {
      breadcrumbs: [{
        label: "Home",
        to: "/"
      }, {
        label: "Robotics Pathway",
        to: "/RoboticsLearningPathwayPage"
      }, {
        label: "Robotics 7+ Years",
        to: "#"
      }, {
        label: "HP2 – Sensing Robots",
        to: null
      }],
      courseCode: "HP2",
      courseName: "HP2 – Sensing Robots",
      tagline: "Explore the world of sensors and intelligent control logic",
      quote: "Robots begin to sense and respond to the environment.",
      heroPrimaryCta: "BOOK CONSULTATION",
      heroSecondaryCta: "VIEW FULL PATHWAY",
      stats: [{
        label: "Age Target",
        value: "From 7 years old",
        icon: BookOpen
      }, {
        label: "Platform Kit",
        value: "WhalesBot AI Module 1S",
        icon: Cpu
      }, {
        label: "Duration",
        value: "12 sessions × 90 mins",
        icon: Calendar
      }, {
        label: "Class Size",
        value: "2–3 students/kit",
        icon: Users
      }, {
        label: "Prerequisite",
        value: "Completed HP1",
        icon: CheckCircle2
      }],
      audienceTitle: "Who is this course for?",
      audienceStatement: "For learners who completed HP1. HP2 extends into infrared and touch sensors with conditional branching logic.",
      audiencePoints: ["Students who mastered fundamental mechanical structures in HP1 and are ready for sensor integration.", "Developing automated control skills utilizing infrared proximity and physical touch sensors.", "Mastering branching algorithmic logic: if, if-else, wait until, variables, and state counters.", "Transitioning from passive motorized machines into responsive autonomous robotic creations."],
      focusHeaders: {
        sensorsLogic: "Sensors & Programming Logic",
        mechanics: "Advanced Mechanics & Structures"
      },
      sensorsLogicItems: [{
        name: "Infrared Sensor",
        desc: "Obstacle and distance detection for environmental awareness"
      }, {
        name: "Touch Sensor",
        desc: "Tactile trigger switch for event response and safety control"
      }, {
        name: "if & if–else",
        desc: "Conditional branching logic governing robot behaviors"
      }, {
        name: "Wait until",
        desc: "Execution hold command waiting for verified signal conditions"
      }, {
        name: "Comparison",
        desc: "Logical comparison operators: greater than, less than, equal"
      }, {
        name: "Counters & Variables",
        desc: "State storage and numerical counters managing runtime values"
      }],
      mechanicsItems: [{
        name: "Bevel gears",
        desc: "Perpendicular bevel gears transmitting motion at 90-degree angles"
      }, {
        name: "Chain drive",
        desc: "Sprocket chain system transmitting torque over longer spans"
      }, {
        name: "Worm gear",
        desc: "Self-locking worm drive delivering extreme mechanical advantage"
      }, {
        name: "Four-bar linkage",
        desc: "Four-bar hinge kinematic assembly producing compound movement"
      }],
      timelineTitle: "12-Session Curriculum (6 Stages)",
      timelineSubtitle: "Structured engineering progression integrating mechanism design and algorithmic sensing",
      timelineStages: [{
        stage: "Stage 1",
        sessions: "Sessions 1 – 2",
        title: "Bevel gears & Chain drive",
        focus: "Explore 90-degree torque redirection with bevel gears and long-distance chain power drives."
      }, {
        stage: "Stage 2",
        sessions: "Sessions 3 – 4",
        title: "Worm gear & Four-bar linkage",
        focus: "Master non-backdrivable self-locking worm drives and planar four-bar kinematic linkages."
      }, {
        stage: "Stage 3",
        sessions: "Sessions 5 – 6",
        title: "Touch sensor & Wait until",
        focus: "Apply physical touch bumpers for mechanical limits paired with Wait until logic triggers."
      }, {
        stage: "Stage 4",
        sessions: "Sessions 7 – 8",
        title: "Infrared sensor & If / If-else",
        focus: "Harness infrared distance sensing coupled with branching conditional if and if-else structures."
      }, {
        stage: "Stage 5",
        sessions: "Sessions 9 – 10",
        title: "Comparisons, Counters & Variables",
        focus: "Synthesize comparative operators with data counters and dynamic state variables."
      }, {
        stage: "Stage 6",
        sessions: "Sessions 11 – 12",
        title: "System Integration & Capstone",
        focus: "Comprehensive integration of mechanics, sensing, and logic, culminating in live defense."
      }],
      methodologyTitle: "4-Step Learning Methodology",
      methodologySubtitle: "Proven iterative STEAM cycle fostering analytical engineering independence",
      methodologySteps: [{
        step: "Step 1",
        title: "Warm-up",
        desc: "Investigate authentic real-world engineering challenges and ignite intuitive problem-solving.",
        icon: Lightbulb
      }, {
        step: "Step 2",
        title: "Build",
        desc: "Construct solid mechanical frames with precision alignment, gear meshing, and structural balance.",
        icon: Wrench
      }, {
        step: "Step 3",
        title: "Code",
        desc: "Design responsive algorithms, wire conditional logic, and tune real-time sensor parameters.",
        icon: Cpu
      }, {
        step: "Step 4",
        title: "Present",
        desc: "Conduct stress-testing, calibrate threshold values, and communicate project solutions confidently.",
        icon: Presentation
      }],
      equipmentTitle: "Learning Platform & Equipment",
      equipmentSubtitle: "International-standard engineering kits equipped across ROBOTO labs",
      equipmentName: "WhalesBot AI Module 1S",
      equipmentCostLabel: "Reference equipment cost",
      equipmentCostValue: "~6,500,000 VNĐ / kit",
      equipmentCostNote: "Note: This is the reference equipment cost for families wishing to purchase a personal kit, not tuition.",
      equipmentPcRequired: "Computer Required",
      equipmentPcNote: "A laptop or desktop computer is required for the WhalesBot visual block-coding suite.",
      groupModelTitle: "Small-Group Learning Model",
      groupModelSubtitle: "Engineered for maximum interactive engagement and practical mastery",
      groupModelIdealTitle: "Ideal: 2 students / kit",
      groupModelIdealDesc: "Maximizes hands-on hardware time, encourages role division (hardware engineer vs software coder), and develops collaborative team dynamics.",
      groupModelOptimizedTitle: "Cost-optimized: 3 students / kit",
      groupModelOptimizedDesc: "A balanced setup optimizing kit equipment investments while ensuring smooth team rotation through construction, coding, and testing.",
      finalProjectTitle: "Example Final Project",
      finalProjectTag: "HP2 CAPSTONE BUILD",
      finalProjectName: "Tiny Factory (Nhà máy tí hon)",
      finalProjectDesc: "The signature HP2 capstone integrating all core curriculum units: mechanics (chain drive, bevel gears, worm gear, four-bar linkage), infrared detection, and touch sensing regulated by branching logic and counting variables.",
      finalProjectHighlights: ["Automated conveyor system powered by 90-degree bevel gears and long-reach chain drives", "Vertical lifter mechanism utilizing self-locking worm and wheel gears", "Infrared distance sensor automatically detecting incoming items and controlling belt stops", "Touch sensor functioning as an emergency safety stop and cycle confirmation trigger", "Variables and counters logging total processed units in real time"],
      pathwayTitle: "Position in Robotics Pathway",
      pathwaySubtitle: "Comprehensive 10-level framework spanning early years through middle school",
      currentBadge: "Current Course",
      nextCourseTitle: "Next Course in Pathway",
      nextCourseBadge: "NEXT STEP: HP3",
      nextCourseName: "HP3 – First Robot Car",
      nextCourseDesc: "Upon mastering stationary sensing and complex drives in HP2, students advance to building mobile wheeled robots featuring differential drive steering, line-tracking sensors, and arena challenges.",
      nextCourseCta: "VIEW HP3 →",
      prevBtn: "← HP1 — Junior Mechanical Engineer",
      nextBtn: "HP3 — First Robot Car →",
      langToggleVI: "Tiếng Việt",
      langToggleEN: "English",
      logicDiagramTitle: "Sensor-Driven Control Logic Diagram (If / Else)",
      logicDiagramSubtitle: "Interactive schematic mapping Sensor Signals → Decision Logic → Robot Actuation",
      sensorLabelIR: "Infrared Proximity Sensor",
      sensorLabelTouch: "Tactile Touch Sensor",
      conditionNode: "Condition: Obstacle < 15cm?",
      actionTrue: "TRUE (If): Stop Motor & Trigger Audio Warning",
      actionFalse: "FALSE (Else): Resume Conveyor Drive Motor"
    }
  };
  const pathwayNodes = [{
    code: "D1",
    path: "/RoboticsCourseD1Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d1.png-wvc-sizes"
  }, {
    code: "D2",
    path: "/RoboticsCourseD2Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d2.png-wvc-sizes"
  }, {
    code: "D3",
    path: "/RoboticsCourseD3Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d3.png-wvc-sizes"
  }, {
    code: "D4",
    path: "/RoboticsCourseD4Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_d4.png-wvc-sizes"
  }, {
    code: "HP1",
    path: "/RoboticsCourseHP1Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp1.webp-wvc-sizes"
  }, {
    code: "HP2",
    path: "/RoboticsCourseHP2Page",
    isCurrent: true,
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp2.png-wvc-sizes"
  }, {
    code: "HP3",
    path: "/RoboticsCourseHP3Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp3.png-wvc-sizes"
  }, {
    code: "HP4",
    path: "/RoboticsCourseHP4Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp4.png-wvc-sizes"
  }, {
    code: "HP5",
    path: "/RoboticsCourseHp5Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp5.png-wvc-sizes"
  }, {
    code: "HP6",
    path: "/RoboticsCourseHP6Page",
    imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png",
    imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-srcset",
    imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_course_hp6.png-wvc-sizes"
  }];
  const t = content[lang];
  return <section id="roboticscoursehp2overview" data-nav="light" className="bg-background text-foreground py-12 md:py-20 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* (1) Top bar: Full 4-level Breadcrumbs & Language Toggle */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 mb-8 border-b border-border">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm">
            {t.breadcrumbs.map((crumb, idx) => {
            const isLast = idx === t.breadcrumbs.length - 1;
            return <div key={crumb.label} data-index={idx} className="inline-flex items-center gap-1.5">
                  {crumb.to ? <Link to={crumb.to} className="text-muted-foreground hover:text-primary transition-colors font-medium">
                      {crumb.label}
                    </Link> : <span className="text-foreground font-semibold">
                      {crumb.label}
                    </span>}
                  {!isLast && <ChevronRight className="size-3.5 text-muted-foreground/60 shrink-0" />}
                </div>;
          })}
          </nav>

          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-card p-1 shadow-2xs self-start md:self-auto">
            <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => setLang("vi")} className="h-8 px-3 text-xs">
              {t.langToggleVI}
            </Button>
            <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => setLang("en")} className="h-8 px-3 text-xs">
              {t.langToggleEN}
            </Button>
          </div>
        </div>

        {/* Hero Course Header Banner with Visual Showcase */}
        <div className="mb-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge variant="default" className="text-sm font-bold px-3 py-1 bg-primary text-primary-foreground">
                {t.courseCode}
              </Badge>
              <Badge variant="secondary" className="text-xs font-medium px-3 py-1">
                WhalesBot AI Module 1S
              </Badge>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
              {t.courseName}
            </h1>
            <p className="mt-3 text-lg text-muted-foreground max-w-3xl leading-relaxed">
              {t.tagline}
            </p>

            {/* (2) Second hero CTA cluster */}
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Button asChild variant="default" size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-7 py-3.5 h-auto shadow-md">
                <Link to="/FreeTrialContactPage">
                  {t.heroPrimaryCta}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-6 py-3.5 h-auto transition-colors">
                <Link to="/RoboticsLearningPathwayPage">
                  {t.heroSecondaryCta}
                </Link>
              </Button>
            </div>
          </div>

          {/* Hero Visual: Infrared & Touch Sensors on WhalesBot Robot */}
          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-[#0d1527] p-2 shadow-xl">
              <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-slate-900 group">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_hp2_sensors_robot.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_hp2_sensors_robot.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_hp2_sensors_robot.png-wvc-sizes" alt="WhalesBot robot equipped with infrared and touch sensors for HP2 course" className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1527] via-transparent to-transparent opacity-80" />

                {/* Technical Blueprint Sensor Callouts */}
                <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#0d1527]/90 backdrop-blur-md px-3 py-1.5 rounded-lg border border-secondary/50 text-white text-xs font-semibold shadow-md">
                  <span className="size-2 rounded-lg bg-secondary animate-pulse" />
                  <span>Infrared Distance Sensor</span>
                </div>

                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-[#0d1527]/90 backdrop-blur-md px-3 py-1.5 rounded-lg border border-accent/60 text-white text-xs font-semibold shadow-md">
                  <span className="size-2 rounded-lg bg-accent" />
                  <span>Tactile Touch Sensor</span>
                </div>
              </div>

              <div className="p-3 flex items-center justify-between text-[11px] text-slate-300 font-mono">
                <span className="flex items-center gap-1.5 text-secondary">
                  <Activity className="size-3.5" />
                  HW: WhalesBot AI Module 1S
                </span>
                <span className="text-slate-400">LAB BLUEPRINT SPEC</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quote Band */}
        <div className="rounded-xl border border-secondary/30 bg-secondary/10 p-6 md:p-8 mb-10 flex items-start gap-4">
          <div className="rounded-lg bg-secondary/20 p-2 shrink-0 text-secondary-foreground">
            <Quote className="size-6 text-primary" />
          </div>
          <div>
            <p className="text-base sm:text-lg md:text-xl font-medium text-foreground italic leading-relaxed">
              &ldquo;{t.quote}&rdquo;
            </p>
          </div>
        </div>

        {/* Key Course Specifications */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {t.stats.map((stat, i) => {
          const IconComp = stat.icon;
          return <Card key={stat.label} data-index={i} className="border border-border bg-card shadow-2xs hover:shadow-xs transition-shadow rounded-xl p-4 flex flex-col justify-between">
                <div className="flex items-center gap-2 mb-2 text-muted-foreground">
                  <IconComp className="size-4 text-primary shrink-0" />
                  <span className="text-xs font-medium">{stat.label}</span>
                </div>
                <div className="text-sm md:text-base font-semibold text-foreground">
                  {stat.value}
                </div>
              </Card>;
        })}
        </div>

        {/* (3) 'Who is this course for?' section */}
        <Card className="border border-border bg-card rounded-2xl shadow-xs mb-12">
          <CardHeader className="pb-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                <Target className="size-5" />
              </div>
              <div>
                <CardTitle className="text-xl md:text-2xl font-bold text-foreground">
                  {t.audienceTitle}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 sm:p-5 mb-6">
              <p className="text-foreground font-semibold text-base sm:text-lg leading-relaxed">
                {t.audienceStatement}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {t.audiencePoints.map((point, idx) => <div key={idx} data-index={idx} className="flex items-start gap-3 p-3.5 rounded-lg border border-border/80 bg-muted/30">
                  <CheckCircle2 className="size-4 text-secondary mt-0.5 shrink-0" />
                  <span className="text-sm text-foreground leading-relaxed">{point}</span>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* Core Syllabus Items: Sensors/Logic & Advanced Mechanics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Sensors & Logic Card */}
          <Card className="border border-border bg-card rounded-2xl shadow-xs">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="rounded-lg bg-primary/10 p-2 text-primary">
                  <Cpu className="size-5" />
                </div>
                <CardTitle className="text-lg md:text-xl font-bold text-foreground">
                  {t.focusHeaders.sensorsLogic}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {t.sensorsLogicItems.map((item, i) => <div key={item.name} data-index={i} className="p-3.5 rounded-lg border border-border/80 bg-muted/40 hover:bg-muted/70 transition-colors">
                    <div className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                      <span className="size-1.5 rounded-lg bg-primary shrink-0" />
                      {item.name}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-snug">
                      {item.desc}
                    </p>
                  </div>)}
              </div>
            </CardContent>
          </Card>

          {/* Advanced Mechanics Card */}
          <Card className="border border-border bg-card rounded-2xl shadow-xs">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="rounded-lg bg-secondary/20 p-2 text-secondary-foreground">
                  <Settings className="size-5 text-secondary-foreground" />
                </div>
                <CardTitle className="text-lg md:text-xl font-bold text-foreground">
                  {t.focusHeaders.mechanics}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {t.mechanicsItems.map((item, i) => <div key={item.name} data-index={i} className="p-3.5 rounded-lg border border-border/80 bg-muted/40 hover:bg-muted/70 transition-colors">
                    <div className="font-semibold text-foreground text-sm flex items-center gap-1.5">
                      <span className="size-1.5 rounded-lg bg-accent shrink-0" />
                      {item.name}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-snug">
                      {item.desc}
                    </p>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* MID-PAGE VISUAL DIAGRAM: Sensor-Driven Robot Behavior (If / Else Logic) */}
        <Card className="border border-border bg-[#0b1322] text-white rounded-2xl shadow-xl p-6 md:p-8 mb-14 overflow-hidden relative">
          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-800">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary/20 text-secondary text-xs font-bold uppercase tracking-wider mb-2">
                  <GitFork className="size-3.5 text-secondary" />
                  <span>ALGORITHMIC CONTROL ARCHITECTURE</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-white font-default">
                  {t.logicDiagramTitle}
                </h2>
                <p className="text-xs sm:text-sm text-slate-300 mt-1">
                  {t.logicDiagramSubtitle}
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-1.5 rounded-lg border border-slate-800 shrink-0">
                <span className="size-2 rounded-lg bg-secondary" />
                <span>ROBOTO LAB CORE v2.4</span>
              </div>
            </div>

            {/* Diagram Content: Image + Interactive Blueprint Nodes */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Diagram Graphic */}
              <div className="lg:col-span-5 rounded-xl overflow-hidden border border-slate-800 bg-slate-950 p-2 shadow-inner">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_sensor_logic_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_sensor_logic_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_sensor_logic_diagram.png-wvc-sizes" alt="Sensor-driven if/else decision logic flow for WhalesBot robot" className="w-full h-auto rounded-lg object-cover" loading="lazy" />
              </div>

              {/* Technical Schematic Flowchart */}
              <div className="lg:col-span-7 space-y-3">
                {/* Step 1: Input */}
                <div className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-900/90 border border-slate-800">
                  <div className="size-10 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/30">
                    <Activity className="size-5" />
                  </div>
                  <div className="flex-1">
                    <span className="text-[10px] font-bold uppercase text-blue-400 tracking-wider">STEP 1: SENSOR INPUT</span>
                    <p className="text-xs sm:text-sm font-semibold text-slate-100">{t.sensorLabelIR} / {t.sensorLabelTouch}</p>
                  </div>
                  <ChevronRight className="size-4 text-slate-500 shrink-0 hidden sm:block" />
                </div>

                {/* Step 2: Decision Node */}
                <div className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-900/90 border border-secondary/40 shadow-sm">
                  <div className="size-10 rounded-lg bg-secondary/20 text-secondary flex items-center justify-center shrink-0 border border-secondary/40">
                    <GitFork className="size-5" />
                  </div>
                  <div className="flex-1">
                    <span className="text-[10px] font-bold uppercase text-secondary tracking-wider">STEP 2: IF / ELSE DECISION</span>
                    <p className="text-xs sm:text-sm font-semibold text-white">{t.conditionNode}</p>
                  </div>
                  <ChevronRight className="size-4 text-slate-500 shrink-0 hidden sm:block" />
                </div>

                {/* Step 3: Branch Actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="p-3 rounded-xl bg-primary/20 border border-primary/40">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="size-2 rounded-lg bg-primary" />
                      <span className="text-[10px] font-bold text-primary-foreground uppercase">IF TRUE</span>
                    </div>
                    <p className="text-xs text-slate-200 leading-snug">{t.actionTrue}</p>
                  </div>

                  <div className="p-3 rounded-xl bg-secondary/15 border border-secondary/30">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="size-2 rounded-lg bg-secondary" />
                      <span className="text-[10px] font-bold text-secondary uppercase">ELSE (FALSE)</span>
                    </div>
                    <p className="text-xs text-slate-200 leading-snug">{t.actionFalse}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* (4) 12-Session timeline grouped into 6 stages */}
        <div className="mb-14">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t.timelineTitle}
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">
              {t.timelineSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {t.timelineStages.map((stage, idx) => <Card key={stage.stage} data-index={idx} className="border border-border bg-card rounded-xl p-5 shadow-2xs hover:border-secondary transition-colors flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-bold">
                      {stage.sessions}
                    </span>
                    <span className="text-xs font-semibold text-muted-foreground">
                      {stage.stage}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-2 leading-snug">
                    {stage.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {stage.focus}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground font-medium">
                  <span>Module 0{idx + 1}</span>
                  <span className="size-2 rounded-lg bg-secondary" />
                </div>
              </Card>)}
          </div>
        </div>

        {/* (5) 4-step learning method section */}
        <div className="mb-14">
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              {t.methodologyTitle}
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">
              {t.methodologySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.methodologySteps.map((step, idx) => {
            const StepIcon = step.icon;
            return <Card key={step.title} data-index={idx} className="border border-border bg-card rounded-xl p-6 shadow-2xs hover:shadow-sm transition-all">
                  <div className="flex items-center justify-between mb-4">
                    <div className="rounded-lg bg-secondary/15 p-3 text-secondary-foreground">
                      <StepIcon className="size-6 text-primary" />
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {step.step}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {step.desc}
                  </p>
                </Card>;
          })}
          </div>
        </div>

        {/* (6) Equipment Card & (7) Small-Group Learning Model Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
          {/* Equipment card */}
          <Card className="border border-border bg-card rounded-2xl shadow-xs">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                  <Cpu className="size-5" />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-primary">
                    {t.equipmentTitle}
                  </span>
                  <CardTitle className="text-xl font-bold text-foreground mt-0.5">
                    {t.equipmentName}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="rounded-xl overflow-hidden border border-border bg-muted/30 aspect-[16/9]">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_robotics_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S robotics kit used in ROBOTO's HP1–HP6 engineering track" className="w-full h-full object-cover" loading="lazy" />
              </div>

              <div className="p-4 rounded-xl border border-secondary/40 bg-secondary/10">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-1.5">
                  <span className="text-xs font-bold uppercase text-secondary-foreground">
                    {t.equipmentCostLabel}
                  </span>
                  <span className="text-base sm:text-lg font-bold text-primary">
                    {t.equipmentCostValue}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t.equipmentCostNote}
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-muted/40 flex items-start gap-3">
                <div className="rounded-md bg-primary/10 p-2 shrink-0 text-primary">
                  <Laptop className="size-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground mb-1">
                    {t.equipmentPcRequired}
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t.equipmentPcNote}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Small-group learning model section */}
          <Card className="border border-border bg-card rounded-2xl shadow-xs">
            <CardHeader className="pb-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-secondary/20 p-2.5 text-secondary-foreground">
                  <Users className="size-5 text-secondary-foreground" />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-secondary">
                    {t.groupModelTitle}
                  </span>
                  <CardTitle className="text-xl font-bold text-foreground mt-0.5">
                    {t.groupModelSubtitle}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="size-2 rounded-lg bg-primary" />
                  <h4 className="text-sm font-bold text-foreground">
                    {t.groupModelIdealTitle}
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t.groupModelIdealDesc}
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-muted/30">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="size-2 rounded-lg bg-accent" />
                  <h4 className="text-sm font-bold text-foreground">
                    {t.groupModelOptimizedTitle}
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t.groupModelOptimizedDesc}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* (8) Final Project Section: 'Nhà máy tí hon' (Tiny Factory) */}
        <Card className="border border-accent/40 bg-card rounded-2xl shadow-xs mb-14 overflow-hidden">
          <CardHeader className="pb-4 border-b border-border bg-accent/5">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-accent/20 p-2.5 text-accent-foreground">
                <Factory className="size-6 text-accent" />
              </div>
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-accent">
                  {t.finalProjectTag}
                </span>
                <CardTitle className="text-xl md:text-2xl font-bold text-foreground mt-0.5">
                  {t.finalProjectName}
                </CardTitle>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
              {t.finalProjectDesc}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {t.finalProjectHighlights.map((highlight, idx) => <div key={idx} data-index={idx} className="flex items-start gap-2.5 p-3.5 rounded-lg border border-border/80 bg-muted/30">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm text-foreground leading-snug">
                    {highlight}
                  </span>
                </div>)}
            </div>
          </CardContent>
        </Card>

        {/* (9) 10-Node Pathway Map with HP2 highlighted */}
        <Card className="border border-border bg-card rounded-2xl shadow-xs p-6 md:p-8 mb-12">
          <div className="mb-6 text-center md:text-left">
            <h2 className="text-xl md:text-2xl font-bold text-foreground">
              {t.pathwayTitle}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t.pathwaySubtitle}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2.5">
            {pathwayNodes.map((node, idx) => {
            const isCurrent = node.isCurrent;
            return <div key={node.code} data-index={idx}>
                  {isCurrent ? <div className="h-full rounded-xl border-2 border-primary bg-primary/10 p-2.5 text-center flex flex-col items-center justify-between gap-2 shadow-2xs">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-primary text-primary-foreground leading-tight">
                        {t.currentBadge}
                      </span>
                      <div className="size-12 rounded-lg overflow-hidden border border-primary/30 shrink-0 bg-background">
                        <img src={node.imgSrc} data-wvc-srcset={node.imgSrcset} data-wvc-sizes={node.imgSizes} alt={`${node.code} robotics course thumbnail`} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <span className="text-sm font-bold text-primary">
                        {node.code}
                      </span>
                    </div> : <Link to={node.path} className="h-full rounded-xl border border-border bg-muted/40 hover:bg-muted hover:border-secondary p-2.5 text-center flex flex-col items-center justify-between gap-2 transition-all group">
                      <div className="size-12 rounded-lg overflow-hidden border border-border group-hover:border-secondary shrink-0 bg-background transition-colors">
                        <img src={node.imgSrc} data-wvc-srcset={node.imgSrcset} data-wvc-sizes={node.imgSizes} alt={`${node.code} robotics course thumbnail`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                      </div>
                      <span className="text-xs text-muted-foreground group-hover:text-foreground font-semibold">
                        {node.code}
                      </span>
                    </Link>}
                </div>;
          })}
          </div>
        </Card>

        {/* (10) 'What's next' card: next course HP3 */}
        <Card className="border border-primary/30 bg-primary/5 rounded-2xl p-6 md:p-8 mb-12 shadow-xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 text-primary text-xs font-bold uppercase mb-2">
                <Rocket className="size-3.5" />
                <span>{t.nextCourseBadge}</span>
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground">
                {t.nextCourseName}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                {t.nextCourseDesc}
              </p>
            </div>
            <Button asChild variant="default" size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 px-6 self-start md:self-center shrink-0 shadow-sm">
              <Link to="/RoboticsCourseHP3Page">
                <span>{t.nextCourseCta}</span>
              </Link>
            </Button>
          </div>
        </Card>

        <Separator className="mb-10" />

        {/* Existing Previous & Next Course Navigation */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
          <Button asChild variant="outline" size="lg" className="justify-start sm:justify-center border-border hover:bg-muted font-medium text-foreground h-12">
            <Link to="/RoboticsCourseHP1Page">
              {t.prevBtn}
            </Link>
          </Button>

          <Button asChild variant="default" size="lg" className="justify-end sm:justify-center bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm h-12">
            <Link to="/RoboticsCourseHP3Page">
              {t.nextBtn}
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}